import {
  Wrap,
  Text,
  Box,
} from '@chakra-ui/react';
import {
  useJourneysUnplannedEvents,
  useRoadAccessLost,
} from '@src/modules/performance/context/reports-api-hooks';
import TornadoBarChart from '@src/modules/performance/components/charts/TornadoBarChart';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Resilience Distributions (ONRC)',
  content: [
    {
      title: 'Incidents vs Journeys impacted',
      description:
  <Text fontSize="inherit" mb={2}>
    This measure shows the number of unplanned road closures with a detour provided, and the number of vehicles affected annually by those closures, summarised by ONRC Category.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from the ONRC input measures in Transport Insights or the Road Closures and Restrictions facility in AWM.
  </Text>,
    },
    {
      title: 'Road Closures vs Vehicles Affected',
      description:
  <Text fontSize="inherit" mb={2}>
    This measure shows the number of unplanned road closures with no detour and the number of vehicles affected by those closures annually, summarised by ONRC Category.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from the ONRC input measures in Transport Insights or the Road Closures and Restrictions facility in AWM.
  </Text>,
    },
  ],
};

function NetworkAvailabilityResilienceDistributionsOnrc() {
  const { data: journeysUnplannedEventsOnrc, isLoading: isLoadingJourneysOnrc } = useJourneysUnplannedEvents('onrc');
  const { data: roadAccessLostOnrc, isLoading: isLoadingRoadAccessOnrc } = useRoadAccessLost('onrc');
  const isLoading = isLoadingJourneysOnrc || isLoadingRoadAccessOnrc;

  // Check if all lockYears are the same
  const allLockYears = [
    journeysUnplannedEventsOnrc?.[0]?.LockYear,
    roadAccessLostOnrc?.[0]?.LockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // ONRC Journeys Unplanned ONRC Chart
  const journeysUnplannedEventsOnrcData = [
    {
      name: 'Incidents',
      type: 'bar',
      data: journeysUnplannedEventsOnrc?.map((item) => ({
        x: item.ONRC,
        y: item.Num_of_Incidents ? item.Num_of_Incidents * -1 : 0,
      })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Journeys Impacted',
      type: 'bar',
      data: journeysUnplannedEventsOnrc?.map((item) => ({
        x: item.ONRC,
        y: item.Num_of_Journeys_Impacted ? item.Num_of_Journeys_Impacted : 0,
      })),
      color: CHART_COLOURS.orange,
    },
  ];

  // ONRC Road Access Lost ONRC Chart
  const roadAccessLostOnrcData = [
    {
      name: 'Road Closures',
      type: 'bar',
      data: roadAccessLostOnrc?.map((item) => ({
        x: item.ONRC,
        y: item.Number_of_Road_Closures ? item.Number_of_Road_Closures * -1 : 0,
      })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Vehicles Affected Annually',
      type: 'bar',
      data: roadAccessLostOnrc?.map((item) => ({
        x: item.ONRC,
        y: item.Number_of_Vehicles_Affected_Annually ? item.Number_of_Vehicles_Affected_Annually : 0,
      })),
      color: CHART_COLOURS.orange,
    },
  ];

  return (
    <ReportSection
      title="Resilience Distributions (ONRC)"
      isLoading={isLoading || !journeysUnplannedEventsOnrc || !roadAccessLostOnrc}
      informationSource={informationSource}
      lockYear={primaryLockYear || null}
    >
      <Wrap>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            lockYear={journeysUnplannedEventsOnrc?.[0]?.LockYear}
            justify="flex-start"
            mb={2}
          />
          )}
          <TornadoBarChart
            title="Incidents vs Journeys impacted"
            categories={[...new Set(journeysUnplannedEventsOnrc?.map((item) => item.ONRC))]}
            series={journeysUnplannedEventsOnrcData}
            paddingRight={50}
            paddingLeft={30}
            width={280}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            lockYear={roadAccessLostOnrc?.[0]?.LockYear}
            justify="flex-start"
            mb={2}
          />
          )}
          <TornadoBarChart
            title="Road closures vs Vehicles affected"
            categories={[...new Set(roadAccessLostOnrc?.map((item) => item.ONRC))]}
            series={roadAccessLostOnrcData}
            paddingRight={50}
            paddingLeft={30}
            width={290}
          />
        </Box>
      </Wrap>
    </ReportSection>
  );
}

export default NetworkAvailabilityResilienceDistributionsOnrc;
